import type { routes, SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import type {
  TEventWithDateRange,
  TPulseEventWithDateRanges,
} from 'App/irrigation-scheduler/utils/store/types'
import moment from 'moment-timezone'

// Copied verbatim from components/IcsDashboard/_utils/parseZoneEvents.ts so Water Monitoring's
// applied/planned irrigation matches the ICS "Planned vs Applied" report exactly (copy-don't-modify).
const INCLUDED_SCHEDULED_STATUSES: SharedTypes.IrrigationScheduler.TEventStatus[] = ['DRAFT', 'PUBLISHED']

export type TParsedZoneEvents = {
  appliedEvents: TEventWithDateRange[]
  scheduledEvents: TEventWithDateRange[]
  scheduledPulseEvents: TPulseEventWithDateRanges[]
}

const EMPTY_PARSED: TParsedZoneEvents = {
  appliedEvents: [],
  scheduledEvents: [],
  scheduledPulseEvents: [],
}

export const parseAllZoneEvents = (
  eventsResponse: routes.IrrigationSchedulerEventsGet.Response | null,
  timezone: string,
): Record<string, TParsedZoneEvents> => {
  if (!eventsResponse?.length) return {}

  const result: Record<string, TParsedZoneEvents> = {}

  eventsResponse.forEach((zoneEmitter) => {
    // Count every actual irrigation run, not just status 'on': external integrations report completed
    // runs as 'completed', and filtering to 'on' dropped them — under-reporting Applied for those zones
    // (matches the Irrigation Scheduler + ICS report fix, PR #1155).
    const appliedEvents: TEventWithDateRange[] = zoneEmitter.actualIrrigationEvents.map((e) => {
      const dateFrom = moment.tz(e.dateFrom, timezone).startOf('minute')
      const isRunning = e.dateTo === null && e.status === 'on'

      // Only stretch to "now" for genuinely-running events; a non-running event missing dateTo gets
      // zero duration rather than being over-counted up to the present.
      const getDateTo = () => {
        if (e.dateTo) return moment.tz(e.dateTo, timezone).startOf('minute')

        return isRunning ? moment.tz(timezone).startOf('minute') : dateFrom.clone()
      }

      return { dateFrom, dateTo: getDateTo() }
    })

    const scheduledEvents: TEventWithDateRange[] = []
    const scheduledPulseEvents: TPulseEventWithDateRanges[] = []

    zoneEmitter.scheduledIrrigationEvents.forEach((event) => {
      if (event.pulseIrrigationEvents?.length) {
        const pulseChildren = event.pulseIrrigationEvents.filter((pe) =>
          INCLUDED_SCHEDULED_STATUSES.includes(pe.status),
        )

        if (pulseChildren.length > 0) {
          scheduledPulseEvents.push({
            pulseIrrigationEvents: pulseChildren.map((pe) => ({
              dateFrom: moment.tz(pe.dateFrom, timezone),
              dateTo: moment.tz(pe.dateTo, timezone),
            })),
          })
        }
      } else if (INCLUDED_SCHEDULED_STATUSES.includes(event.status)) {
        scheduledEvents.push({
          dateFrom: moment.tz(event.dateFrom, timezone),
          dateTo: moment.tz(event.dateTo, timezone),
        })
      }
    })

    result[zoneEmitter.irrigationZoneEmitterId] = { appliedEvents, scheduledEvents, scheduledPulseEvents }
  })

  return result
}

export const getZoneEvents = (
  parsedEvents: Record<string, TParsedZoneEvents>,
  irrigationZoneEmitterId: string,
): TParsedZoneEvents => parsedEvents[irrigationZoneEmitterId] ?? EMPTY_PARSED
